import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import QuantitySelector from 'components/commons/QuantitySelector';
import DiscountBanner from 'components/commons/DiscountBanner';
import { formatCurrencyARS } from 'helpers/currency';
import { sendErrorNotification, sendSuccessNotification } from 'helpers/notifications';

import './ProductSingle.scss';

const ProductSingle = ({
  images = [], item, onProductCopyClick, onQuantityChange, singleProductFullUrl,
  cartItemDescriptions = [], cartItemSubDescriptions = [], colors = [], combinations = {},
  wholesale, setWholesale, talles = [], selectedColor, handleColorSelect,
  getValidPriceRange, show = true, showShippingQuote = false, cuotasMP = [],
  onOpenPaymentModal, showMPButton, autoDiscounts = [], paymentMethods = [],
}) => {
  const [activeImage, setActiveImage] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const history = useHistory();
  const productImages = useMemo(() => images.filter(Boolean), [images]);
  const chosenImage = productImages[activeImage] || productImages[0];
  const availableColors = useMemo(() => {
    const uniqueColors = new Map();
    colors.forEach((color) => {
      const id = String(color.stock_description_id);
      if (!uniqueColors.has(id)) uniqueColors.set(id, color);
    });
    return Array.from(uniqueColors.values()).filter((color) => (
      Number(color.stock) > 0
      || (combinations[color.stock_description_id] || []).some((option) => Number(option.stock) > 0)
    ));
  }, [colors, combinations]);
  const hasVariants = Array.isArray(item.stock_product_descriptions) && item.stock_product_descriptions.length > 0;
  const totalQuantity = [...cartItemDescriptions, ...cartItemSubDescriptions]
    .filter((entry) => Number(entry.wholesale?.id || 0) === Number(wholesale.id || 0))
    .reduce((total, entry) => total + (Number(entry.quantity) || 0), 0);

  useEffect(() => setActiveImage(0), [images, selectedColor]);

  useEffect(() => {
    if (!isZoomOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsZoomOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isZoomOpen]);

  useEffect(() => {
    if (selectedColor == null && availableColors.length > 0 && Object.keys(combinations).length > 0) {
      handleColorSelect(availableColors[0]);
    }
  }, [selectedColor, availableColors, combinations, handleColorSelect]);

  const getOptionState = (description) => {
    const get = (entries) => entries.find((entry) => (
      Number(entry.descriptionId) === Number(description.id)
      && Number(entry.wholesale?.id || 0) === Number(wholesale.id || 0)
    ));
    return {
      storage: get(cartItemDescriptions),
      storageSub: get(cartItemSubDescriptions),
      othersWholesale: cartItemDescriptions.filter((entry) => (
        Number(entry.descriptionId) === Number(description.id)
        && Number(entry.wholesale?.id || 0) !== Number(wholesale.id || 0)
      )),
      othersWholesaleSub: cartItemSubDescriptions.filter((entry) => (
        Number(entry.descriptionId) === Number(description.id)
        && Number(entry.wholesale?.id || 0) !== Number(wholesale.id || 0)
      )),
    };
  };

  const handleColor = (color) => {
    handleColorSelect(color);
    const colorImages = [color.image_1, color.image_2].filter(Boolean);
    const firstImage = productImages.findIndex((image) => colorImages.includes(image));
    if (firstImage >= 0) setActiveImage(firstImage);
  };

  const renderDescriptionQuantity = (description) => {
    const state = getOptionState(description);
    const quantity = Number(state.storage?.quantity ?? state.storageSub?.quantity) || 0;
    const otherQuantities = [...state.othersWholesale, ...state.othersWholesaleSub]
      .map((entry) => Number(entry.quantity) || 0);
    return (
      <div className="product-single__variant" key={description.id}>
        <div className="product-single__variant-info">
          <span>{description.stock_descriptions?.name || 'Opción'}</span>
          {Number(description.price) > 0 && <strong>{formatCurrencyARS(description.price)}</strong>}
        </div>
        <QuantitySelector
          description={description}
          limit={Number(description.stock) || 0}
          onQuantityChange={onQuantityChange}
          quantity={quantity}
          quantityOthersWholesale={otherQuantities.length ? otherQuantities : [0]}
          show={show}
        />
      </div>
    );
  };

  const renderOptions = () => {
    if (!hasVariants) {
      return (
        <div className="product-single__quantity">
          <span className="product-single__field-label">Cantidad</span>
          <QuantitySelector
            limit={Number(item.stock) || 0}
            onQuantityChange={onQuantityChange}
            quantity={Number(cartItemDescriptions.find((entry) => Number(entry.wholesale?.id || 0) === Number(wholesale.id || 0))?.quantity) || 0}
            quantityOthersWholesale={cartItemDescriptions.filter((entry) => Number(entry.wholesale?.id || 0) !== Number(wholesale.id || 0)).map((entry) => entry.quantity)}
            show={show}
            isTextil={Number(item.unit_type) === 2}
            unit_type={item.unit_type}
          />
        </div>
      );
    }

    if (availableColors.length > 0) {
      const activeColor = availableColors.find((color) => Number(color.stock_description_id) === Number(selectedColor)) || availableColors[0];
      const options = combinations[activeColor?.stock_description_id] || [];
      const colorIsVariant = options.length === 0;
      const colorState = getOptionState(activeColor);
      return (
        <div className="product-single__options">
          <div className="product-single__field-label">Color</div>
          <div className="product-single__swatches" role="group" aria-label="Elegir color">
            {availableColors.map((color) => {
              const selected = Number(color.stock_description_id) === Number(activeColor?.stock_description_id);
              return (
                <button
                  className={`product-single__swatch${selected ? ' is-selected' : ''}`}
                  type="button"
                  key={color.id}
                  onClick={() => handleColor(color)}
                  aria-pressed={selected}
                  title={color.stock_descriptions?.name}
                >
                  <span className="product-single__swatch-dot" style={{ backgroundColor: color.stock_descriptions?.rgb || '#e5e7eb' }} />
                  <span>{color.stock_descriptions?.name || 'Color'}</span>
                </button>
              );
            })}
          </div>
          {colorIsVariant ? (
            <div className="product-single__variants">
              <span className="product-single__field-label">Cantidad</span>
              <QuantitySelector
                description={activeColor}
                limit={Number(activeColor.stock) || 0}
                onQuantityChange={onQuantityChange}
                quantity={Number(colorState.storage?.quantity ?? colorState.storageSub?.quantity) || 0}
                quantityOthersWholesale={[
                  ...colorState.othersWholesale,
                  ...colorState.othersWholesaleSub,
                ].map((entry) => Number(entry.quantity) || 0)}
                show={show}
              />
            </div>
          ) : (
            <div className="product-single__variants">
              <span className="product-single__field-label">Elegí talle y cantidad</span>
              <div className="product-single__variant-grid">{options.filter((option) => Number(option.stock) > 0).map(renderDescriptionQuantity)}</div>
            </div>
          )}
        </div>
      );
    }

    const variantOptions = talles.length > 0
      ? talles
      : item.stock_product_descriptions.filter((description) => Number(description.stock) > 0);
    return (
      <div className="product-single__options">
        <span className="product-single__field-label">{talles.length > 0 ? 'Elegí talle y cantidad' : 'Elegí una opción'}</span>
        <div className="product-single__variant-grid">{variantOptions.filter((option) => Number(option.stock) > 0).map(renderDescriptionQuantity)}</div>
      </div>
    );
  };

  const getPrice = () => {
    if (wholesale.bool) {
      const selected = item.product_wholesaler.find((entry) => Number(entry.id) === Number(wholesale.id));
      return selected ? selected.price * selected.quantity : item.price;
    }
    if (typeof getValidPriceRange === 'function' && talles.some((size) => Number(size.price) > 0)) {
      return getValidPriceRange();
    }
    return Number(item.sale) > 0 ? item.sale : item.price;
  };
  const currentPrice = getPrice();
  const formattedPrice = typeof currentPrice === 'string' && currentPrice.includes('$')
    ? currentPrice
    : formatCurrencyARS(currentPrice);

  const handleAddToCart = () => {
    if (!show) {
      sendErrorNotification('Iniciá sesión para consultar el precio y comprar.');
      return;
    }
    if (totalQuantity < 1) {
      sendErrorNotification('Elegí una cantidad antes de agregar el producto al carrito.');
      return;
    }
    if (wholesale.bool && wholesale.quantity > 0) {
      const wholesaleRows = [...cartItemDescriptions, ...cartItemSubDescriptions].filter((entry) => (
        Number(entry.wholesale?.id || 0) === Number(wholesale.id)
        && Number(entry.quantity) > 0
      ));
      const meetsPack = totalQuantity >= wholesale.quantity && totalQuantity % wholesale.quantity === 0;
      const sizeIds = new Set(wholesaleRows.map((entry) => entry.descriptionId).filter((id) => id != null));
      const balancedCurve = !wholesale.curva || (
        sizeIds.size === Number(wholesale.quantity)
        && wholesaleRows.every((entry) => Number(entry.quantity) === Number(wholesaleRows[0]?.quantity))
      );
      if (!meetsPack || !balancedCurve) {
        sendErrorNotification(wholesale.curva
          ? 'Completá la curva con la misma cantidad para cada talle.'
          : `Agregá cantidades en múltiplos de ${wholesale.quantity}.`);
        return;
      }
    }
    sendSuccessNotification('Producto agregado al carrito.');
    history.push('/carrito');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
    <article className="product-single" data-testid="product-single-page">
      <section className="product-single__gallery" aria-label="Fotos del producto">
        <div className="product-single__image-frame">
          {chosenImage ? <button type="button" className="product-single__image-button" onClick={() => setIsZoomOpen(true)} aria-label="Ampliar imagen del producto"><img src={chosenImage} alt={item.name} className="product-single__image" /></button> : <div className="product-single__placeholder">{item.name}</div>}
          {productImages.length > 1 && (
            <>
              <button type="button" className="product-single__arrow is-prev" aria-label="Foto anterior" onClick={() => setActiveImage((activeImage - 1 + productImages.length) % productImages.length)}>‹</button>
              <button type="button" className="product-single__arrow is-next" aria-label="Foto siguiente" onClick={() => setActiveImage((activeImage + 1) % productImages.length)}>›</button>
            </>
          )}
        </div>
        {productImages.length > 1 && <div className="product-single__thumbnails">{productImages.map((image, index) => <button type="button" key={`${image}-${index}`} className={`product-single__thumbnail${index === activeImage ? ' is-active' : ''}`} onClick={() => setActiveImage(index)} aria-label={`Ver imagen ${index + 1}`} aria-pressed={index === activeImage}><img src={image} alt="" /></button>)}</div>}
      </section>

      <section className="product-single__details">
        <div className="product-single__eyebrow">{item.code && <span>SKU {item.code}</span>}<CopyToClipboard text={singleProductFullUrl} onCopy={onProductCopyClick}><button className="product-single__copy" type="button">Copiar enlace</button></CopyToClipboard></div>
        <h1 className="product-single__title">{item.name}</h1>
        <div className="product-single__price-block">
          {!wholesale.bool && Number(item.sale) > 0 && Number(item.price) > Number(item.sale) && <span className="product-single__discount">{Math.round(100 - (item.sale / item.price) * 100)}% OFF</span>}
          <div className="product-single__price">{show ? formattedPrice : 'Iniciá sesión para ver el precio'}</div>
          {!wholesale.bool && Number(item.sale) > 0 && Number(item.price) > Number(item.sale) && <div className="product-single__old-price">{formatCurrencyARS(item.price)}</div>}
          {wholesale.bool && <span className="product-single__price-note">Precio por {wholesale.quantity} unidades</span>}
        </div>
        {(item.description || item.short_description) && <p className="product-single__description">{item.short_description || item.description}</p>}

        {item.product_wholesaler.length > 0 && <div className="product-single__section"><span className="product-single__field-label">Presentación</span><div className="product-single__wholesale">{[{ id: 0, bool: false, quantity: 0, curva: false, label: 'Por unidad' }, ...item.product_wholesaler.map((option) => ({ ...option, bool: true, label: option.comboName || `Por ${option.quantity} unidades` }))].map((option) => <button type="button" key={option.id} className={`product-single__pill${Number(wholesale.id) === Number(option.id) ? ' is-selected' : ''}`} aria-pressed={Number(wholesale.id) === Number(option.id)} onClick={() => setWholesale({ id: option.id, bool: option.bool, quantity: option.quantity, curva: option.curva })}>{option.label}</button>)}</div></div>}

        <div className="product-single__section">
          {show ? renderOptions() : <p className="product-single__login-note">Iniciá sesión para ver las opciones y comprar.</p>}
        </div>
        <DiscountBanner autoDiscounts={autoDiscounts} paymentMethods={paymentMethods} />
        {showShippingQuote && <div className="product-single__shipping"><span aria-hidden="true">↗</span> Envíos disponibles para todo el país</div>}
        <button className="product-single__add" type="button" onClick={handleAddToCart}>{show ? 'Agregar al carrito' : 'Consultar precio'}<span aria-hidden="true">→</span></button>
        {showMPButton && cuotasMP.length > 0 && <button className="product-single__payments" type="button" onClick={onOpenPaymentModal}>Ver medios de pago y cuotas</button>}
        <Link className="product-single__back" to="/productos">Seguir viendo productos</Link>
      </section>
    </article>
    {isZoomOpen && chosenImage && createPortal(
      <div className="product-single-zoom" role="dialog" aria-modal="true" aria-label={`Imagen ampliada: ${item.name}`} onClick={() => setIsZoomOpen(false)}>
        <button className="product-single-zoom__close" type="button" aria-label="Cerrar imagen ampliada" onClick={() => setIsZoomOpen(false)}>×</button>
        <img className="product-single-zoom__image" src={chosenImage} alt={item.name} onClick={(event) => event.stopPropagation()} />
      </div>,
      document.body,
    )}
    </>
  );
};

ProductSingle.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string),
  item: PropTypes.shape().isRequired,
  onProductCopyClick: PropTypes.func.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  singleProductFullUrl: PropTypes.string.isRequired,
  cartItemDescriptions: PropTypes.arrayOf(PropTypes.shape()),
  cartItemSubDescriptions: PropTypes.arrayOf(PropTypes.shape()),
  colors: PropTypes.arrayOf(PropTypes.shape()),
  combinations: PropTypes.shape(),
  wholesale: PropTypes.shape().isRequired,
  setWholesale: PropTypes.func.isRequired,
  talles: PropTypes.arrayOf(PropTypes.shape()),
  selectedColor: PropTypes.number,
  handleColorSelect: PropTypes.func.isRequired,
  getValidPriceRange: PropTypes.func.isRequired,
  show: PropTypes.bool,
  showShippingQuote: PropTypes.bool,
  cuotasMP: PropTypes.arrayOf(PropTypes.shape()),
  onOpenPaymentModal: PropTypes.func.isRequired,
  showMPButton: PropTypes.bool,
  autoDiscounts: PropTypes.arrayOf(PropTypes.shape()),
  paymentMethods: PropTypes.arrayOf(PropTypes.shape()),
};

export default ProductSingle;
